import { Button, type IAdditionalClassNames, Input, useToast } from '@netfront/ui-library';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { COMMIT_EMAIL_OR_PHONE_NUMBER_CHANGE_MUTATION, useDomain, useLogout } from '@netfront/gelada-identity-library';
import { useEffect } from 'react';
import { getValidClassNames } from '@/utils';
import { useRouter } from 'next/router';
import { useMutation } from '@netfront/apollo-providers';

interface IConfirmCodeFormFields {
  code: string;
}

export interface InputEmailValidationCodeFormProps extends IAdditionalClassNames {
  code: string | null;
}

export const InputEmailValidationCodeForm = ({ additionalClassNames, code }: InputEmailValidationCodeFormProps) => {
  const { getDomain } = useDomain();
  const { handleToastError, handleToastSuccess } = useToast();
  const router = useRouter();
  const { handleLogout } = useLogout({
    onCompleted: () => {
      router.push('/login?emailVerified=true');
    },
  });

  const { handleMutation: handleCommitEmailOrPhoneNumberChange } = useMutation({
    apiName: 'gelada',
    mutation: COMMIT_EMAIL_OR_PHONE_NUMBER_CHANGE_MUTATION,
    onCompleted: async () => {
      handleLogout({ domain: getDomain(), path: '/' });
    },
    onError: (error) => {
      handleToastError({
        error,
        shouldUseFriendlyErrorMessage: true,
      });
    },
  });

  const formik = useFormik<IConfirmCodeFormFields>({
    initialValues: {
      code: code || '',
    },
    validationSchema: yup.object().shape({
      code: yup.string().required('Please enter the validation code sent to your email'),
    }),
    onSubmit: async ({ code: token }: IConfirmCodeFormFields) => {
      await handleCommitEmailOrPhoneNumberChange({
        token,
      });
    },
  });

  const { errors, touched, handleBlur, handleChange, handleSubmit, isSubmitting, submitCount, values, isValid } = formik;

  useEffect(() => {
    if (code && !isSubmitting && submitCount === 0) {
      handleSubmit();
    }
  }, []);

  return (
    <form
      onSubmit={handleSubmit}
      className={getValidClassNames({
        [String(additionalClassNames)]: Boolean(additionalClassNames),
      })}
    >
      <Input
        errorMessage={touched.code && errors.code ? errors.code : ''}
        id="code"
        labelText="Validation Code"
        name="code"
        type="text"
        value={values.code}
        onChange={handleChange}
        onBlur={handleBlur}
        isRequired
      />

      <Button
        isDisabled={isSubmitting || !isValid}
        isLoading={isSubmitting}
        text="Submit code"
        type="submit"
        variant="secondary"
      />
    </form>
  );
};
